import {useCallback, useEffect, useId, useRef, useState} from 'react';
import {Languages} from 'lucide-react';
import {
  OnDeviceTranslationClient,
  onDeviceTranslationSupport,
  translationErrorMessage,
  translationLanguages,
  type TranslationLanguage,
  type TranslationProgress,
  type TranslationResult,
  type TranslationSourceLanguage,
  type TranslationText,
} from './lib/on-device-translation';
import './content-translation.css';

export type ContentTranslationProps = {
  /** Stable listing/profile identifier. A different identifier requires fresh consent. */
  contentKey: string;
  /** Public prose only: never send private account fields, messages, prices or technical tables. */
  texts: readonly TranslationText[];
  initialTargetLanguage?: TranslationLanguage;
  /** Known brand/model names that must remain verbatim even inside the public prose. */
  protectedTerms?: readonly string[];
};

export default function ContentTranslation({contentKey, texts, initialTargetLanguage = 'it', protectedTerms = []}: ContentTranslationProps) {
  const support = onDeviceTranslationSupport();
  const [targetLanguage, setTargetLanguage] = useState<TranslationLanguage>(initialTargetLanguage);
  const [sourceLanguage, setSourceLanguage] = useState<TranslationSourceLanguage>(support.canDetectLanguage ? 'auto' : 'it');
  const [enabledKey, setEnabledKey] = useState<string | null>(null);
  const [viewOriginal, setViewOriginal] = useState(false);
  const [progress, setProgress] = useState<TranslationProgress | null>(null);
  const [error, setError] = useState<{signature: string; message: string} | null>(null);
  const [result, setResult] = useState<{signature: string; value: TranslationResult} | null>(null);
  const client = useRef<OnDeviceTranslationClient | null>(null);
  const abort = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const submittedSignature = useRef('');
  const previousContentKey = useRef(contentKey);
  const fieldId = useId();
  const signature = JSON.stringify([contentKey, texts, targetLanguage, sourceLanguage, protectedTerms]);
  const enabled = enabledKey === contentKey;
  const currentResult = result?.signature === signature ? result.value : null;
  const currentError = error?.signature === signature ? error.message : '';
  const hasText = texts.some(text => text.text.trim().length > 0);

  const runTranslation = useCallback(() => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const request = ++generation.current;
    submittedSignature.current = signature;
    setProgress({phase: sourceLanguage === 'auto' ? 'detecting' : 'translating'});
    setError(null);
    setViewOriginal(false);
    client.current ??= new OnDeviceTranslationClient();
    return client.current.translate({
      texts,
      sourceLanguage,
      targetLanguage,
      protectedTerms,
      signal: controller.signal,
      onProgress: next => {
        if (!controller.signal.aborted && request === generation.current) setProgress(next);
      },
    }).then(value => {
      if (!controller.signal.aborted && request === generation.current) setResult({signature, value});
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted && request === generation.current) {
        setResult(null);
        setError({signature, message: translationErrorMessage(failure)});
      }
    }).finally(() => {
      if (!controller.signal.aborted && request === generation.current) setProgress(null);
    });
  }, [signature, texts, sourceLanguage, targetLanguage, protectedTerms]);

  useEffect(() => {
    if (previousContentKey.current !== contentKey) {
      previousContentKey.current = contentKey;
      abort.current?.abort();
      generation.current += 1;
      client.current?.dispose();
      client.current = null;
      submittedSignature.current = '';
      setEnabledKey(null);
      setProgress(null);
      setResult(null);
      setError(null);
      setViewOriginal(false);
      return;
    }
    // Automatic updates are allowed only after the explicit click, on this same item.
    if (enabled && submittedSignature.current !== signature) void runTranslation();
  }, [contentKey, enabled, signature, runTranslation]);

  useEffect(() => () => {
    generation.current += 1;
    abort.current?.abort();
    client.current?.dispose();
    client.current = null;
  }, []);

  function showOriginal() {
    abort.current?.abort();
    generation.current += 1;
    setProgress(null);
    setEnabledKey(null);
    setError(null);
    setViewOriginal(true);
  }

  const status = viewOriginal ? 'Testo originale. Traduzione automatica disattivata.' : progress?.phase === 'downloading'
    ? `Preparazione del modello locale${progress.progress === undefined ? '…' : ` · ${Math.round(progress.progress * 100)}%`}`
    : progress?.phase === 'detecting' ? 'Riconoscimento della lingua sul dispositivo…'
    : progress ? 'Traduzione sul dispositivo…'
    : currentResult?.unchanged ? 'Il testo è già nella lingua scelta.'
    : currentResult ? `Traduzione automatica · ${translationLanguages.find(language => language.code === targetLanguage)?.label}` : '';

  return <section className="content-translation" aria-label="Traduzione del testo pubblico">
    <div className="content-translation-heading"><Languages size={17} aria-hidden="true"/><strong>Leggi nella tua lingua</strong><span>Solo sul dispositivo</span></div>
    {!support.supported ? <p className="content-translation-hint">{support.message}</p> : <>
      <div className="content-translation-controls">
        <label htmlFor={`${fieldId}-target`}><span>Traduci in</span><select id={`${fieldId}-target`} value={targetLanguage} onChange={event => setTargetLanguage(event.target.value as TranslationLanguage)}>{translationLanguages.map(language => <option key={language.code} value={language.code}>{language.label}</option>)}</select></label>
        <label htmlFor={`${fieldId}-source`}><span>Lingua originale</span><select id={`${fieldId}-source`} value={sourceLanguage} onChange={event => setSourceLanguage(event.target.value as TranslationSourceLanguage)}><option value="auto" disabled={!support.canDetectLanguage}>Rileva automaticamente{!support.canDetectLanguage ? ' · non disponibile' : ''}</option>{translationLanguages.map(language => <option key={language.code} value={language.code}>{language.label}</option>)}</select></label>
        <button type="button" className="content-translation-start" disabled={!hasText || Boolean(progress)} onClick={() => {setEnabledKey(contentKey); void runTranslation();}}>{currentError ? 'Riprova' : currentResult && !viewOriginal ? 'Traduci di nuovo' : 'Traduci sul dispositivo'}</button>
        {(enabled || currentResult || progress) && <button type="button" className="content-translation-original" onClick={showOriginal}>Mostra originale</button>}
      </div>
      <p className="content-translation-hint">Si attiva solo al tuo clic. Il browser può scaricare pacchetti lingua; il testo non viene inviato a un servizio di traduzione. Dopo l’attivazione, la lingua scelta si applica automaticamente a questo contenuto.</p>
      {!support.canDetectLanguage && <p className="content-translation-hint">Il rilevamento automatico non è disponibile: controlla e seleziona la lingua originale.</p>}
      <p className="content-translation-status" role="status" aria-live="polite">{status}</p>
      {progress?.phase === 'downloading' && progress.progress !== undefined && <progress max={1} value={progress.progress} aria-label="Preparazione del modello di traduzione"/>}
      {currentError && <p className="content-translation-error" role="alert">{currentError}</p>}
      {(viewOriginal || currentResult) && <div className="content-translation-output" lang={viewOriginal ? undefined : currentResult?.targetLanguage}>
        {(viewOriginal ? texts.filter(text => text.text.trim()) : currentResult?.texts || []).map(text => <div key={text.id}><strong>{text.label}{viewOriginal ? ' · originale' : ''}</strong><p>{text.text}</p></div>)}
        {!viewOriginal && currentResult && !currentResult.unchanged && <small>La traduzione può contenere errori. Per accordi, condizioni e dettagli tecnici fa riferimento l’originale; marca, modello e scheda tecnica non vengono sostituiti.</small>}
      </div>}
    </>}
  </section>;
}
